// One explicit time zone everywhere (spec/dashboard.md section 3.4): every rendered instant carries its zone label.
import { useCallback, useEffect, useState } from 'react';

export type DisplayZone = 'utc' | 'env:dev' | 'env:stage' | 'local';

const KEY = 'hue.timezone';
const listeners = new Set<() => void>();

export function loadZone(): DisplayZone {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'env:dev' || v === 'env:stage' || v === 'local' ? v : 'utc';
  } catch {
    return 'utc';
  }
}

export function saveZone(zone: DisplayZone): void {
  try {
    localStorage.setItem(KEY, zone);
  } catch {
    // ignore
  }
  for (const l of Array.from(listeners)) l();
}

/** Game-time offsets of the environments that may carry one (dev and stage; production is UTC, D-24). */
export interface ZoneContext {
  offsets?: Partial<Record<'dev' | 'stage', number>>;
}

export function useTimeZone(): [DisplayZone, (zone: DisplayZone) => void] {
  const [zone, setZone] = useState<DisplayZone>(() => loadZone());
  useEffect(() => {
    const l = () => setZone(loadZone());
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);
  const set = useCallback((z: DisplayZone) => saveZone(z), []);
  return [zone, set];
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function offsetLabel(seconds: number): string {
  const sign = seconds < 0 ? '-' : '+';
  const abs = Math.abs(seconds);
  return `${sign}${pad(Math.floor(abs / 3600))}:${pad(Math.floor((abs % 3600) / 60))}`;
}

/** The label that follows every date in the chosen zone. */
export function zoneLabel(zone: DisplayZone, ctx: ZoneContext = {}): string {
  switch (zone) {
    case 'utc':
      return 'game time · UTC';
    case 'env:dev':
      return `game time · dev (${offsetLabel(ctx.offsets?.dev ?? 0)})`;
    case 'env:stage':
      return `game time · stage (${offsetLabel(ctx.offsets?.stage ?? 0)})`;
    case 'local': {
      // the browser zone's short name (EEST, GMT+3, PDT); the IANA name when the runtime has no short name for it
      const short = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value;
      return `local · ${short ?? Intl.DateTimeFormat().resolvedOptions().timeZone}`;
    }
  }
}

/** Renders unix seconds (or an ISO string) in the display zone: `2026-10-21 14:05`. */
export function formatInstant(value: number | string | null | undefined, zone: DisplayZone = loadZone(), ctx: ZoneContext = {}, opts: { seconds?: boolean } = {}): string {
  if (value === null || value === undefined || value === '') return '—';
  const ms = typeof value === 'number' ? value * 1000 : Date.parse(value);
  if (Number.isNaN(ms)) return String(value);
  if (zone === 'local') {
    const d = new Date(ms);
    const base = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    return opts.seconds ? `${base}:${pad(d.getSeconds())}` : base;
  }
  const offset = zone === 'env:dev' ? (ctx.offsets?.dev ?? 0) : zone === 'env:stage' ? (ctx.offsets?.stage ?? 0) : 0;
  const d = new Date(ms + offset * 1000);
  const base = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  return opts.seconds ? `${base}:${pad(d.getUTCSeconds())}` : base;
}

/** A date with its zone label, the way every page shows one. */
export function formatInstantWithZone(value: number | string | null | undefined, zone: DisplayZone = loadZone(), ctx: ZoneContext = {}): string {
  const text = formatInstant(value, zone, ctx);
  return text === '—' ? text : `${text} ${zoneLabel(zone, ctx)}`;
}

/** A coarse relative time for status lines (`just now`, `5 min ago`, `2 h ago`, `3 d ago`); the instant goes in a tooltip. */
export function relativeTime(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '—';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  const s = Math.max(0, Math.floor((now - ms) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
}

/** Parses `YYYY-MM-DDTHH:mm` typed in the display zone back to unix seconds. */
export function parseLocalInput(text: string, zone: DisplayZone, ctx: ZoneContext = {}): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(text.trim());
  if (!m) return null;
  const [, y, mo, d, h, mi, s] = m;
  if (zone === 'local') {
    const date = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s ?? 0));
    return Math.floor(date.getTime() / 1000);
  }
  const offset = zone === 'env:dev' ? (ctx.offsets?.dev ?? 0) : zone === 'env:stage' ? (ctx.offsets?.stage ?? 0) : 0;
  return Math.floor(Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s ?? 0)) / 1000) - offset;
}

const DURATION_UNITS: Record<string, number> = { d: 86400, h: 3600, m: 60, s: 1 };

/**
 * Reads a typed or pasted duration back to whole seconds (spec/dashboard.md section 6.4, the duration widget):
 * `1d 12h 30m 15s`, `90m`, `2h`, `1.5h` (units d / h / m / s and their long words, case-insensitive, spaces optional),
 * `2:30:00` (h:m:s), `45:00` (m:s), and a bare number as seconds. Null when nothing parses.
 */
export function parseDuration(text: string): number | null {
  const t = text.trim().toLowerCase();
  if (t === '') return null;
  if (/^\d+$/.test(t)) return Number(t);
  const colon = /^(\d+)\s*:\s*(\d{1,2})(?:\s*:\s*(\d{1,2}))?$/.exec(t);
  if (colon) {
    const [, a, b, c] = colon;
    if (c === undefined) {
      if (Number(b) > 59) return null;
      return Number(a) * 60 + Number(b);
    }
    if (Number(b) > 59 || Number(c) > 59) return null;
    return Number(a) * 3600 + Number(b) * 60 + Number(c);
  }
  const token = /(\d+(?:\.\d+)?|\.\d+)\s*(days?|d|hours?|hrs?|h|minutes?|mins?|m|seconds?|secs?|s)(?![a-z])/gy;
  let total = 0;
  let pos = 0;
  let any = false;
  const rest = t.replace(/[\s,]+/g, ' ');
  while (pos < rest.length) {
    if (rest[pos] === ' ') {
      pos += 1;
      continue;
    }
    token.lastIndex = pos;
    const m = token.exec(rest);
    if (!m) return null;
    total += Number(m[1]) * (DURATION_UNITS[(m[2] ?? '').charAt(0)] ?? 0);
    any = true;
    pos = token.lastIndex;
  }
  return any ? Math.round(total) : null;
}

/** `3d 12h`, `45m`, `0s`; the owner decides what 0 reads as (`zeroMeans`). */
export function formatDuration(seconds: number | null | undefined, zeroMeans?: string): string {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds === 0 && zeroMeans) return zeroMeans;
  const parts: string[] = [];
  let rest = Math.abs(Math.floor(seconds));
  const d = Math.floor(rest / 86400);
  rest -= d * 86400;
  const h = Math.floor(rest / 3600);
  rest -= h * 3600;
  const m = Math.floor(rest / 60);
  const s = rest - m * 60;
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  if (s || parts.length === 0) parts.push(`${s}s`);
  return (seconds < 0 ? '-' : '') + parts.join(' ');
}
